import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminCurveIntermediate,
  AdminInsertCurveIntermediateBody,
  AdminInsertSystemCurveIntermediateBody,
  AdminUpdateCurveIntermediateBody,
  AdminUpdateSystemCurveIntermediateBody,
} from "./types/curveIntermediates";

export const useCurvesIntermediatesApi = () => {
  const api = useApi();

  const useAdminCurvesIntermediates = useCallback(
    (query: PaginationQuery, signal: AbortSignal, systemId?: string) => {
      const url = buildUrl(
        systemId
          ? `/admin/settings/patrol/systems/${systemId}/curves_intermediates`
          : `/admin/settings/patrol/curves_intermediates`,
        query,
      );
      return useSWR<Paginated<AdminCurveIntermediate>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminCurveIntermediate>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminCurveIntermediate = useCallback(
    async (body: AdminInsertCurveIntermediateBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/curves_intermediates`;
      const id = await api.post<{ id: string }, AdminInsertCurveIntermediateBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const insertAdminSystemCurveIntermediate = useCallback(
    async (body: AdminInsertSystemCurveIntermediateBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/curves_intermediates`;
      const id = await api.post<{ id: string }, AdminInsertSystemCurveIntermediateBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const getAdminCurveIntermediate = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/curves_intermediates`;
      return useSWRMutation<AdminCurveIntermediate, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminCurveIntermediate>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const updateAdminCurveIntermediate = useCallback(
    async (curveIntermediateId: string, body: AdminUpdateCurveIntermediateBody): Promise<void> => {
      const url = `/admin/settings/patrol/curves_intermediates/${curveIntermediateId}`;
      await api.put<AdminUpdateCurveIntermediateBody>(url, body);
      mutateCache([`/admin/settings/patrol/curves_intermediates`, url]);
    },
    [api],
  );

  const updateAdminSystemCurveIntermediate = useCallback(
    async (curveIntermediateId: string, body: AdminUpdateSystemCurveIntermediateBody): Promise<void> => {
      const url = `/admin/settings/patrol/curves_intermediates/${curveIntermediateId}`;
      await api.put<AdminUpdateSystemCurveIntermediateBody>(url, body);
      mutateCache([`/admin/settings/patrol/curves_intermediates`, url]);
    },
    [api],
  );

  const updateAdminCurveIntermediateVisibility = useCallback(
    async (curveIntermediateId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/curves_intermediates/${curveIntermediateId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/curves_intermediates"]);
    },
    [api],
  );

  const deleteAdminCurveIntermediate = useCallback(
    async (curveIntermediateId: string): Promise<void> => {
      const url = `/admin/settings/patrol/curves_intermediates/${curveIntermediateId}`;
      await api.del(url);
      mutateCache([`/admin/settings/patrol/curves_intermediates`, url]);
    },
    [api],
  );

  return {
    useAdminCurvesIntermediates,
    insertAdminCurveIntermediate,
    insertAdminSystemCurveIntermediate,
    getAdminCurveIntermediate,
    updateAdminCurveIntermediate,
    updateAdminSystemCurveIntermediate,
    updateAdminCurveIntermediateVisibility,
    deleteAdminCurveIntermediate,
  };
};
